import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { BsGrid } from "react-icons/bs";
import { AiOutlineHome } from "react-icons/ai";
import { PiUserCircle } from "react-icons/pi";
import { IoLogOutOutline, IoNotificationsOutline } from "react-icons/io5";
import { HiOutlineMenu, HiX } from "react-icons/hi";
import axios from "../../store/api/axios";
import { SITE_NAME } from "../../utils/Constants";

// "about 2 hours ago" instead of "Thu, 02 Oct 2026 07:12:44 GMT"; the exact
// time is still available as a hover tooltip.
const timeAgo = (value: string) => {
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : formatDistanceToNow(date, { addSuffix: true });
};

const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative flex transition-opacity ${isActive
        ? "font-bold after:absolute after:left-0 after:right-0 after:-bottom-[7px] after:h-0.5 after:rounded-full after:bg-white"
        : "opacity-[.85] hover:opacity-100"}`;

const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex border-l-2 pl-2 ${isActive ? "font-bold border-white" : "border-transparent opacity-[.85] hover:opacity-100"}`;

const NotificationList: React.FC<{ notifications: any[]; unreadCount: number }> = ({ notifications, unreadCount }) => (
    <>
        <div className="flex justify-between items-baseline px-4 pt-3.5 pb-2">
            <span className="font-semibold text-[15px]">Notifications</span>
            {unreadCount > 0 && <span className="text-xs text-gray-70">{unreadCount} new</span>}
        </div>
        {notifications.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-gray-500">No notifications yet</p>
        ) : (
            <div className="pb-2">
                {notifications.map((notification) => (
                    <div
                        key={notification.id}
                        className={`flex gap-3 items-start px-4 py-2.5 text-sm ${!notification.is_read ? "bg-primary/[0.04]" : ""}`}
                    >
                        <span className="shrink-0 w-8 h-8 rounded-full bg-secondary text-primary grid place-items-center">
                            <IoNotificationsOutline />
                        </span>
                        <p className="flex-1 min-w-0 leading-snug text-gray-800 break-words">
                            {notification.message}
                            <span
                                className="block text-xs text-gray-70 mt-0.5"
                                title={new Date(notification.created_at).toLocaleString()}
                            >
                                {timeAgo(notification.created_at)}
                            </span>
                        </p>
                        {!notification.is_read && (
                            <span className="shrink-0 w-2 h-2 rounded-full bg-primary mt-1.5" aria-label="Unread" />
                        )}
                    </div>
                ))}
            </div>
        )}
    </>
);

const Header: React.FC = () => {

    const [notifications, setNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [auth, setAuth] = useState<any>(() => {
        const raw = localStorage.getItem("auth_data");
        return raw ? JSON.parse(raw) : null;
    });

    useEffect(() => {
        const sync = () => {
            const raw = localStorage.getItem("auth_data");
            setAuth(raw ? JSON.parse(raw) : null);
        };
        window.addEventListener("auth-changed", sync);
        const onStorage = (e: StorageEvent) => { if (e.key === "auth_data") sync(); };
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener("auth-changed", sync);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    useEffect(() => {
        // Was `[]` (mount-only), so it only ever read the *initial* auth
        // value — a user who logged in via in-app navigation (no full page
        // reload) never got their notifications fetched at all.
        if (auth?.id) {
            fetchNotifications();
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [auth?.id]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!open && notifications.some((n) => !n.is_read)) {
            const markAllAsRead = async () => {
                try {
                    await Promise.all(
                        notifications
                            .filter((n) => !n.is_read)
                            .map((n) =>
                                axios.patch(`/notifications/${n.id}/read/`, { is_read: true })
                            )
                    );
                    fetchNotifications();
                } catch (err) {
                    console.error("Error marking notifications as read:", err);
                }
            };
            markAllAsRead();
        }
        // Intentionally NOT depending on `notifications`: this should only
        // fire on the open->close transition (mark-as-read-on-dismiss). If a
        // new notification arrives while the dropdown is already closed, it
        // must not be silently marked read before the user ever sees it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const fetchNotifications = async () => {
        try {
            const res = await axios.get("/notifications/");
            setNotifications(res.data);
            setUnreadCount(res.data.filter((n: any) => !n.is_read).length);
        } catch (err) {
            console.error("Error fetching notifications:", err);
        }
    };

    const logout = () => {
        localStorage.removeItem("auth_data");
        localStorage.removeItem("access_token");
        window.dispatchEvent(new Event("auth-changed"));
    };

    const unreadBadge = unreadCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full px-[5px]">
            {unreadCount}
        </span>
    );

    return (
        <header className="bg-primary text-white py-4 px-6 w-full">
            <div className="container mx-auto flex justify-between items-center max-w-[1200px] gap-4 768:gap-6 1020:gap-8">
                {/* The name is long: smaller on narrow screens, and on tablets with the
                    full logged-in menu it wraps to a second line instead of pushing the menu. */}
                <Link to="/" className="min-w-0 text-lg 480:text-xl 1020:text-2xl font-bold leading-tight">{SITE_NAME}</Link>
                <button
                    className="768:hidden shrink-0 flex items-center text-2xl"
                    onClick={() => setIsMenuOpen(prev => !prev)}
                    aria-label="Toggle navigation"
                >
                    {isMenuOpen ? <HiX /> : <HiOutlineMenu />}
                </button>
                <nav className="hidden 768:flex shrink-0 items-center space-x-4 1020:space-x-6">
                    <NavLink to="/" end className={desktopLinkClass}>Home<AiOutlineHome className="my-auto ml-1 text-lg" /></NavLink>
                    <NavLink to="/projects" className={desktopLinkClass}>Projects<BsGrid className="my-auto ml-1" /></NavLink>
                    {auth?.id && (
                        <NavLink to={`/profile/${auth.id}`} className={desktopLinkClass}>Profile<PiUserCircle className="my-auto ml-1 text-xl" /></NavLink>
                    )}
                    {auth?.id && (
                        <div className="relative" ref={dropdownRef}>
                            <button onClick={() => setOpen(!open)} className="flex opacity-[.85] hover:opacity-100">
                                Notifications
                                <div className='relative'>
                                    <IoNotificationsOutline className="my-auto ml-1 text-xl" />
                                    {unreadBadge}
                                </div>
                            </button>

                            {open && (
                                <div className="absolute -right-2 mt-2 w-80 bg-white text-black rounded-3xl shadow-lg z-50 max-h-96 overflow-y-auto">
                                    <NotificationList notifications={notifications} unreadCount={unreadCount} />
                                </div>
                            )}
                        </div>
                    )}

                    {!auth?.id ?
                        <div className="flex items-center gap-2">
                            <Link to="/login" className="rounded-full px-4 py-1.5 text-sm ring-[1.5px] ring-inset ring-white/70 hover:bg-white/10">Log in</Link>
                            <Link to="/signup" className="rounded-full px-4 py-1.5 text-sm font-semibold bg-white text-primary hover:bg-white/90">Sign up</Link>
                        </div>
                        :
                        <Link
                            to="/"
                            onClick={logout}
                            className="flex opacity-[.85] hover:opacity-100">
                            Logout<IoLogOutOutline className="my-auto ml-1 text-xl rotate-180" />
                        </Link>
                    }
                </nav>
            </div>

            {isMenuOpen && (
                <div className="768:hidden mt-3">
                    <nav className="flex flex-col space-y-3 bg-primary/90 rounded-xl p-4 max-w-[1200px] mx-auto">
                        <NavLink
                            to="/"
                            end
                            className={mobileLinkClass}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Home
                            <AiOutlineHome className="my-auto ml-1 text-lg" />
                        </NavLink>

                        <NavLink
                            to="/projects"
                            className={mobileLinkClass}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Projects
                            <BsGrid className="my-auto ml-1" />
                        </NavLink>

                        {auth?.id && (
                            <NavLink
                                to={`/profile/${auth.id}`}
                                className={mobileLinkClass}
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Profile
                                <PiUserCircle className="my-auto ml-1 text-xl" />
                            </NavLink>
                        )}

                        {auth?.id && (
                            <div className="mt-2">
                                <button
                                    onClick={() => setOpen(!open)}
                                    className="flex justify-between items-center border-l-2 border-transparent pl-2 opacity-[.85] hover:opacity-100"
                                >
                                    <span>Notifications</span>
                                    <div className="relative">
                                        <IoNotificationsOutline className="my-auto ml-1 text-xl" />
                                        {unreadBadge}
                                    </div>
                                </button>

                                {open && (
                                    <div className="mt-2 bg-white text-black rounded-2xl shadow-lg max-h-80 overflow-y-auto">
                                        <NotificationList notifications={notifications} unreadCount={unreadCount} />
                                    </div>
                                )}
                            </div>
                        )}


                        {!auth?.id ? (
                            <div className="flex gap-2 pt-1">
                                <Link
                                    to="/login"
                                    className="rounded-full px-4 py-1.5 text-sm ring-[1.5px] ring-inset ring-white/70"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/signup"
                                    className="rounded-full px-4 py-1.5 text-sm font-semibold bg-white text-primary"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Sign up
                                </Link>
                            </div>
                        ) : (
                            <button
                                onClick={() => {
                                    logout();
                                    setIsMenuOpen(false);
                                }}
                                className="flex text-left border-l-2 border-transparent pl-2 opacity-[.85] hover:opacity-100"
                            >
                                Logout
                                <IoLogOutOutline className="my-auto ml-1 text-xl rotate-180" />
                            </button>
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
